import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Paper,
    Button,
    Pagination,
    CircularProgress,
    Chip,
    Tabs,
    Tab,
    useTheme,
    alpha
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Settings as SettingsIcon,
    Assignment as AssignmentIcon,
    Person as PersonIcon,
    CreditCard as CreditCardIcon,
    Alarm as AlarmIcon,
    Done as DoneIcon
} from '@mui/icons-material';
import { format, isToday, isYesterday } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useNotificacao } from '../context/NotificacaoContext';
import notificacaoService, { Notificacao, NotificacoesResponse } from '../services/notificacaoService';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`notificacoes-tabpanel-${index}`}
            aria-labelledby={`notificacoes-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `notificacoes-tab-${index}`,
        'aria-controls': `notificacoes-tabpanel-${index}`,
    };
}

const Notificacoes: React.FC = () => {
    const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
    const { notificacoesNaoLidas, marcarComoLida: marcarNotificacaoComoLida, marcarTodasComoLidas: marcarTodasNotificacoesComoLidas } = useNotificacao();
    const [carregando, setCarregando] = useState<boolean>(false);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [page, setPage] = useState<number>(1);
    const [tabValue, setTabValue] = useState<number>(0);
    const theme = useTheme();
    const navigate = useNavigate();

    // Carregar notificações paginadas
    const carregarNotificacoes = async (pagina: number = 0) => {
        try {
            setCarregando(true);
            const resposta: NotificacoesResponse = await notificacaoService.buscarNotificacoes(pagina);
            setNotificacoes(resposta.content);
            setTotalPages(resposta.totalPages);
        } catch (error) {
            console.error('Erro ao carregar notificações:', error);
        } finally {
            setCarregando(false);
        }
    };

    // Carregar notificações ao montar o componente
    useEffect(() => {
        carregarNotificacoes();
    }, []);

    // Formatar data relativa
    const formatarData = (dataString: string) => {
        const data = new Date(dataString);

        if (isToday(data)) {
            return `Hoje às ${format(data, 'HH:mm')}`;
        } else if (isYesterday(data)) {
            return `Ontem às ${format(data, 'HH:mm')}`;
        } else {
            return format(data, "dd 'de' MMMM 'às' HH:mm", { locale: pt });
        }
    };

    // Obter ícone para o tipo de notificação
    const getIcone = (tipo: string) => {
        switch (tipo) {
            case 'SUCCESS':
                return <CheckCircleIcon color="success" />;
            case 'WARNING':
                return <WarningIcon color="warning" />;
            case 'ERROR':
                return <ErrorIcon color="error" />;
            case 'SYSTEM':
                return <SettingsIcon color="secondary" />;
            case 'SUBSCRIPTION':
                return <CreditCardIcon color="primary" />;
            case 'ACCOUNT':
                return <PersonIcon color="primary" />;
            case 'TASK':
                return <AssignmentIcon color="primary" />;
            case 'REMINDER':
                return <AlarmIcon color="primary" />;
            case 'INFO':
            default:
                return <InfoIcon color="primary" />;
        }
    };

    // Obter texto para o tipo de notificação
    const getTipoTexto = (tipo: string) => {
        switch (tipo) {
            case 'SUCCESS':
                return 'Sucesso';
            case 'WARNING':
                return 'Aviso';
            case 'ERROR':
                return 'Erro';
            case 'SYSTEM':
                return 'Sistema';
            case 'SUBSCRIPTION':
                return 'Assinatura';
            case 'ACCOUNT':
                return 'Conta';
            case 'TASK':
                return 'Tarefa';
            case 'REMINDER':
                return 'Lembrete';
            case 'INFO':
            default:
                return 'Informação';
        }
    };

    // Obter cor para o tipo de notificação
    const getCorChip = (tipo: string) => {
        switch (tipo) {
            case 'SUCCESS':
                return 'success';
            case 'WARNING':
                return 'warning';
            case 'ERROR':
                return 'error';
            case 'SYSTEM':
                return 'secondary';
            case 'SUBSCRIPTION':
            case 'ACCOUNT':
            case 'TASK':
            case 'REMINDER':
            case 'INFO':
            default:
                return 'primary';
        }
    };

    // Marcar notificação como lida
    const marcarComoLida = async (notificacao: Notificacao) => {
        try {
            await marcarNotificacaoComoLida(notificacao.guid);

            // Atualizar listas
            setNotificacoes(prev =>
                prev.map(n => n.guid === notificacao.guid ? { ...n, lida: true } : n)
            );

            // Navegar se tiver link
            if (notificacao.linkAcao) {
                navigate(notificacao.linkAcao);
            }
        } catch (error) {
            console.error('Erro ao marcar como lida:', error);
        }
    };

    // Marcar todas as notificações como lidas
    const marcarTodasComoLidas = async () => {
        try {
            await marcarTodasNotificacoesComoLidas();

            // Atualizar listas
            setNotificacoes(prev =>
                prev.map(n => ({ ...n, lida: true }))
            );

            // Atualizar para a primeira aba
            setTabValue(0);
        } catch (error) {
            console.error('Erro ao marcar todas como lidas:', error);
        }
    };

    // Mudar de página
    const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        carregarNotificacoes(value - 1); // API usa paginação base-0
    };

    // Mudar de aba
    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Renderizar lista de notificações
    const renderNotificacaoItem = (notificacao: Notificacao) => (
        <Paper
            key={notificacao.guid}
            elevation={1}
            sx={{
                mb: 2,
                p: 0,
                backgroundColor: notificacao.lida
                    ? 'background.paper'
                    : alpha(theme.palette.primary.main, 0.05),
                borderLeft: `4px solid ${notificacao.lida
                    ? theme.palette.divider
                    : notificacaoService.getCorNotificacao(notificacao.tipoNotificacao)}`,
                overflow: 'hidden'
            }}
        >
            <ListItem
                alignItems="flex-start"
                secondaryAction={
                    !notificacao.lida && (
                        <IconButton
                            edge="end"
                            aria-label="marcar como lida"
                            onClick={() => marcarComoLida(notificacao)}
                        >
                            <DoneIcon />
                        </IconButton>
                    )
                }
            >
                <ListItemIcon>
                    {getIcone(notificacao.tipoNotificacao)}
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography
                                variant="subtitle1"
                                component="span"
                                sx={{ fontWeight: notificacao.lida ? 'normal' : 600 }}
                            >
                                {notificacao.titulo}
                            </Typography>
                            <Chip
                                label={getTipoTexto(notificacao.tipoNotificacao)}
                                size="small"
                                color={getCorChip(notificacao.tipoNotificacao) as any}
                                variant="outlined"
                            />
                        </Box>
                    }
                    secondary={
                        <>
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ mt: 1, mb: 2 }}
                            >
                                {notificacao.mensagem}
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 1
                            }}>
                                <Typography variant="caption" color="text.secondary">
                                    {formatarData(notificacao.dataEnvio)}
                                </Typography>

                                {notificacao.linkAcao && notificacao.textoAcao && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => marcarComoLida(notificacao)}
                                    >
                                        {notificacao.textoAcao}
                                    </Button>
                                )}
                            </Box>
                        </>
                    }
                />
            </ListItem>
        </Paper>
    );

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Notificações
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Gerencie suas notificações e mantenha-se atualizado sobre as novidades da plataforma.
                </Typography>
            </Box>

            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
            }}>
                <Tabs
                    value={tabValue}
                    onChange={handleChangeTab}
                    aria-label="abas de notificações"
                    textColor="secondary"
                    indicatorColor="secondary"
                >
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                Não lidas
                                {notificacoesNaoLidas.length > 0 && (
                                    <Chip
                                        label={notificacoesNaoLidas.length}
                                        color="error"
                                        size="small"
                                        sx={{ ml: 1, height: 20, minWidth: 20 }}
                                    />
                                )}
                            </Box>
                        }
                        {...a11yProps(0)}
                    />
                    <Tab label="Todas" {...a11yProps(1)} />
                </Tabs>

                {notificacoesNaoLidas.length > 0 && (
                    <Button
                        variant="outlined"
                        startIcon={<DoneIcon />}
                        onClick={marcarTodasComoLidas}
                    >
                        Marcar todas como lidas
                    </Button>
                )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {carregando ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TabPanel value={tabValue} index={0}>
                        {notificacoesNaoLidas.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                <Typography variant="h6" color="text.secondary">
                                    Você não possui notificações não lidas
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ width: '100%' }}>
                                {notificacoesNaoLidas.map(renderNotificacaoItem)}
                            </List>
                        )}
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        {notificacoes.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <InfoIcon color="disabled" sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                <Typography variant="h6" color="text.secondary">
                                    Você não possui notificações
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ width: '100%' }}>
                                {notificacoes.map(renderNotificacaoItem)}
                            </List>
                        )}

                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handleChangePage}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </TabPanel>
                </>
            )}
        </Container>
    );
};

export default Notificacoes;
